/**
 * The dashboard shell: a persistent rail on desktop, a temporary drawer below `md`.
 *
 * Navigation is built from `shippedItems` filtered by the user's permissions, so a salesperson
 * sees three items and an owner sees twenty — and neither ever sees a control that fails on click.
 *
 * Two structural decisions worth naming:
 *
 * - The rail scrolls independently of the content. An owner's nav is long enough to overflow a
 *   laptop viewport, and a rail that scrolls the page with it loses the logo and the account block.
 * - The page title is *not* here. It belongs to `PageHeader` in the content area, where it can sit
 *   with its description and its primary action instead of competing with the product's own chrome.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, NavLink, useLocation, useNavigation } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import {
  Permission,
  applyBrand,
  applyShopTheme,
  can,
  selectActiveShopId,
  selectTheme,
  selectUser,
  useListShopsQuery,
} from '@pos/shared';
import { neutralBrand } from '@pos/tokens';

import { useAppSelector } from '@/app/hooks';
import { ShopMark } from '@/shared/ui/ShopMark';
import { navIcon } from '@/shared/navigation/navIcons';
import { NAV_GROUPS, shippedItems, type NavGroup, type NavItem } from '@/shared/navigation/navItems';

import { AccountMenu } from './AccountMenu.js';
import { GlobalSearch } from './GlobalSearch.js';
import { MpesaBanner } from './MpesaBanner.js';
import { SessionExpiryBanner } from './SessionExpiryBanner.js';
import { ShopSwitcher } from './ShopSwitcher.js';

const RAIL = 252;

export function DashboardShell({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const permanent = useMediaQuery(theme.breakpoints.up('md'));
  const user = useAppSelector(selectUser);
  const activeShopId = useAppSelector(selectActiveShopId);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // An owner can be acting on a shop that is not their own, and the branding has to follow the
  // shop they are writing into — that is the whole point of it being on screen. Everyone else
  // never leaves their home shop, holds no `shop.act_any`, and so never runs this query: for
  // them the session's own theme is both the answer and already loaded.
  const mayActAny = can(user, Permission.ShopActAny);
  const { data: shops } = useListShopsQuery(undefined, { skip: !mayActAny });

  const activeShop = mayActAny ? shops?.find((shop) => shop.id === activeShopId) : undefined;
  const activeShopName = activeShop?.name ?? user?.homeShopName ?? '—';

  // While an owner's shop list is still in flight the session's theme is the honest answer — it
  // is the shop they landed on — so the rail never flashes an unstyled or default look first.
  const activeShopTheme = activeShop?.themeCode ?? user?.homeShopTheme;

  // Fires in the same commit the shop-scoped queries below re-key on `activeShopId`, so the
  // colour and the data change together, not a visible beat apart. Also keyed on the user's
  // toggle: choosing "system" removes their saved choice, and the precedence then puts the
  // shop's own default scheme back — which only happens if this re-runs.
  const themeChoice = useAppSelector(selectTheme);

  // The tab rides along: it answers the same question the look does, and the address bar already
  // names a shop before the session does. `activeShopName` is not used — its em-dash placeholder
  // is for a table cell, not a window title.
  useEffect(() => {
    applyShopTheme(activeShopTheme);
    applyBrand(activeShop?.name ?? user?.homeShopName ?? neutralBrand.dashboard);
  }, [activeShopTheme, activeShop?.name, user?.homeShopName, themeChoice]);

  // The content region is one element for the whole session, so it kept the last screen's scroll:
  // a tester opened Transfers from halfway down Reports, saw no page header and no form top, and
  // wrote that the page "would not scroll". A new screen starts at its top.
  const scrollRegion = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRegion.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const visible = shippedItems.filter(
    (item) =>
      item.permission === undefined ||
      can(user, ...(typeof item.permission === 'string' ? [item.permission] : item.permission)),
  );

  const grouped = groupsOf(visible);

  // Which section the current URL lives in. Opening that one and closing the rest is what takes
  // the rail from twenty rows to about eight without hiding where the user already is.
  const activeGroup = useMemo(
    () =>
      grouped.find(([, items]) =>
        items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)),
      )?.[0],
    [grouped, pathname],
  );

  const [openGroup, setOpenGroup] = useState<NavGroup | undefined>(activeGroup);

  // Follows navigation, but does not fight a deliberate expansion: this only fires when the URL
  // moves into a different section, so a user who opened Money to look around keeps it open.
  useEffect(() => {
    if (activeGroup !== undefined) setOpenGroup(activeGroup);
  }, [activeGroup]);

  const nav = (
    <Box
      role="navigation"
      aria-label="Main"
      sx={{ width: RAIL, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Toolbar sx={{ gap: 1.25, minHeight: { xs: 64, md: 68 } }}>
        {/* The shop over the door, not the software vendor. A manager at Diamond who opens this
            and reads another company's name at the top of their own rail has been told, wrongly,
            whose books they are looking at. */}
        <ShopMark themeCode={activeShopTheme} size={26} />
        <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: 17 }} noWrap>
          {activeShopName}
        </Typography>
      </Toolbar>

      {/* The only scrolling region in the rail, so the logo above and the account block below stay put. */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 1 }}>
        {grouped.map(([group, items]) => {
          // "Home" is a single item with no section to speak of — a collapsible header over one
          // row is a control that costs a click and saves nothing.
          const collapsible = group !== 'Home' && items.length > 1;
          const expanded = !collapsible || openGroup === group;

          const rows = items.map((item) => (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              onClick={() => {
                setOpen(false);
              }}
              sx={{
                borderRadius: 2,
                minHeight: 36,
                px: 1.5,
                pl: collapsible ? 2.5 : 1.5,
                color: 'var(--pos-text-secondary)',
                '&:hover': { bgcolor: 'var(--pos-surface-sunken)' },
                // Moss for the selected state — identity and structure, a quiet fill rather than
                // a saturated bar. The rail must not compete with the data beside it.
                '&.active': {
                  bgcolor: 'var(--pos-brand-subtle)',
                  color: 'var(--pos-brand-subtle-text)',
                  '& .MuiListItemText-primary': { fontWeight: 700 },
                  '&:hover': { bgcolor: 'var(--pos-brand-subtle)' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>{navIcon(item.icon)}</ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{ primary: { fontSize: 13, fontWeight: 500 } }}
              />
            </ListItemButton>
          ));

          return (
            <List key={group} data-tour={`nav-${group}`} dense disablePadding sx={{ px: 1, pb: 0.5 }}>
              {collapsible && (
                <ListItemButton
                  onClick={() => {
                    setOpenGroup((current) => (current === group ? undefined : group));
                  }}
                  aria-expanded={expanded}
                  sx={{
                    borderRadius: 2,
                    minHeight: 32,
                    px: 1.5,
                    color: 'var(--pos-text-muted)',
                    '&:hover': { bgcolor: 'var(--pos-surface-sunken)' },
                  }}
                >
                  <ListItemText
                    primary={group}
                    slotProps={{
                      primary: {
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                      },
                    }}
                  />
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 18,
                      transition: 'var(--pos-transition-control)',
                      transform: expanded ? 'none' : 'rotate(-90deg)',
                    }}
                  />
                </ListItemButton>
              )}

              {collapsible ? (
                // `unmountOnExit` is deliberately off: the NavLinks stay mounted so a collapsed
                // section still matches the route, and expanding it does not re-render the rail.
                <Collapse in={expanded} timeout="auto">
                  {rows}
                </Collapse>
              ) : (
                rows
              )}
            </List>
          );
        })}
      </Box>

      <Divider />

      {/* Who is signed in, at the foot of the rail — where a shop's shared terminal gets checked. */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }} noWrap>
          {user?.fullName ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {[user?.roleName, user?.homeShopName].filter((part) => Boolean(part)).join(' · ')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    // A fixed height, not a minimum: `<main>`'s content region is the only thing that scrolls,
    // which is what lets a table fill the space under its page header and scroll inside itself
    // instead of every screen guessing at `calc(100dvh - 330px)` for the chrome above it.
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{
          bgcolor: 'var(--pos-surface-raised)',
          borderBottom: '1px solid var(--pos-border-subtle)',
          width: permanent ? `calc(100% - ${RAIL}px)` : '100%',
          ml: permanent ? `${RAIL}px` : 0,
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: { xs: 64, md: 68 } }}>
          {!permanent && (
            <IconButton
              data-tour="menu"
              edge="start"
              aria-label="Open navigation"
              onClick={() => {
                setOpen(true);
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <GlobalSearch />

          <Box sx={{ flex: 1 }} />

          <ShopSwitcher />

          <Tooltip title="Help">
            <IconButton
              data-tour="help"
              aria-label="Help"
              size="small"
              component={RouterLink}
              to="/help"
              sx={{ color: 'text.secondary' }}
            >
              <HelpGlyph />
            </IconButton>
          </Tooltip>

          <AccountMenu />
        </Toolbar>
      </AppBar>

      {permanent ? (
        <Drawer
          variant="permanent"
          sx={{
            width: RAIL,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: RAIL,
              borderRight: '1px solid var(--pos-border-subtle)',
              bgcolor: 'var(--pos-surface-base)',
            },
          }}
        >
          {nav}
        </Drawer>
      ) : (
        <Drawer
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { bgcolor: 'var(--pos-surface-base)' } }}
        >
          {nav}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: 8, md: 8.5 },
          bgcolor: 'var(--pos-surface-base)',
        }}
      >
        {/* Above the content on every page, and not dismissible. A banner somebody can close is
            a banner that is closed, and the fact it carries — that these payments are not real
            money — is the one nobody may miss. */}
        <NavigationProgress />
        <MpesaBanner />
        <SessionExpiryBanner />

        {/* The scroll region. `minHeight: 0` is what stops a long page pushing the column
            taller than the viewport and taking the rail's scroll with it. */}
        <Box
          ref={scrollRegion}
          className="scroll-region"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Grouped in `NAV_GROUPS` order rather than in whatever order the items happen to be declared —
 * the rail's sections should not reshuffle because somebody appended a nav entry.
 */
function groupsOf(items: readonly NavItem[]): [NavGroup, NavItem[]][] {
  return NAV_GROUPS.map((group): [NavGroup, NavItem[]] => [
    group,
    items.filter((item) => item.group === group),
  ]).filter(([, groupItems]) => groupItems.length > 0);
}

/** MUI has no outlined question-mark-in-circle at the weight the rail uses. */
function HelpGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M9.6 9.3a2.4 2.4 0 1 1 3.3 2.2c-.6.3-.9.8-.9 1.4v.4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16.4" r="1" fill="currentColor" />
    </svg>
  );
}

/**
 * A thin bar under the header while the next screen loads.
 *
 * Each screen's code is fetched the first time it is opened, and until it arrives the router keeps
 * the old screen up — correctly, a blank flash is worse. But with nothing moving, a tester on the
 * shop's connection clicked Stock, saw the home page stay put, and wrote "nothing happened" three
 * times. Held back 150ms so a screen that is already loaded never flickers it.
 */
function NavigationProgress() {
  const loading = useNavigation().state !== 'idle';

  return (
    <Box sx={{ position: 'relative', height: 0, zIndex: 1 }} aria-hidden={!loading}>
      {loading && (
        <LinearProgress
          aria-label="Opening the page"
          sx={{
            position: 'absolute',
            inset: '0 0 auto 0',
            height: 3,
            opacity: 0,
            animation: 'pos-nav-progress-in 1ms linear 150ms forwards',
            '@keyframes pos-nav-progress-in': { to: { opacity: 1 } },
          }}
        />
      )}
    </Box>
  );
}
